import React, { useState } from "react";
import { Button, Checkbox, Collapse, ConfigProvider, Divider, Input, Modal, Spin, Tabs, Timeline, Tooltip, Typography } from "antd";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AddLinkIcon from "@mui/icons-material/AddLink";
import { InfoCircleOutlined, PauseOutlined, PlusOutlined } from '@ant-design/icons';
import tabby_logo from "../../../assets/images/tabby_logo.png";
import network_logo from "../../../assets/images/network_logo.jpg";
import mamo_logo from "../../../assets/images/mamo_logo.jpg";

const FieldRow = ({ label, children }) => (
    <div className="flex gap-1">
        <div className="flex justify-between" style={{ width: "130px" }}>
            <span>{label}</span><span>:</span>
        </div>
        <div style={{ flex: 1 }}>{children}</div>
    </div>
);

const PayByLinkModal = ({
    openLinkGen, handleCloseLinkGen,
    // Mamo props
    linkTitle, setLinkTitleHandler, linkDescription, setLinkDescriptionHandler,
    linkAmount, setLinkAmount, handle_tabby_enable_change, enable_tabby,
    pay_by_link_loading, handleLinkGen, linkVATPercent, setLinkVATPercent,
    disablePercentEdit, linkFeePercent, setLinkFeePercent,
    theLink, copyToClipboardModern, load_send_link_to_wati, handleSendWatiLink,
    // Network props
    networkFirstName, setNetworkFirstName,
    networkLastName, setNetworkLastName,
    networkEmail, setNetworkEmail,
    networkSkipEmail, setNetworkSkipEmail,
    networkItems, setNetworkItems,
    networkAmount, setNetworkAmount,
    networkVATPercent, setNetworkVATPercent,
    networkFeePercent, setNetworkFeePercent,
    handleNetworkLinkGen, network_pay_loading,
    networkLink, handleNetworkSendWatiLink, load_send_network_link_to_wati,
    networkPaymentHistory, networkHistoryLoading,
    handleNetworkSendWatiLinkForRecord, sendingNetworkIds,
}) => {
    const [activePayTab, setActivePayTab] = useState("network");

    const networkTotal = (
        parseFloat(networkAmount || 0) * (1 + parseFloat(networkVATPercent || 0) / 100) * (1 + parseFloat(networkFeePercent || 0) / 100)
    ).toFixed(2);

    const requestButtonTheme = {
        components: {
            Button: {
                defaultHoverColor: "#ffffff",
                defaultColor: "#ffffff",
                defaultHoverBg: "#3be368",
                defaultBg: "#31bd56",
            },
        },
    };

    const networkTabContent = (
        <div className="gap-2 grid border rounded drop-shadow p-2" style={{ backgroundColor: "#f7f7f7" }}>
            <div className="border rounded p-2 gap-2 grid" style={{ backgroundColor: "#fbfbfb" }}>
                <FieldRow label="Query ID">
                    <Input value={openLinkGen?.data?.query_id || ""} disabled />
                </FieldRow>
                <FieldRow label="First Name">
                    <Input
                        placeholder="First Name"
                        allowClear
                        required
                        value={networkFirstName}
                        onChange={e => setNetworkFirstName(e.target.value)}
                    />
                </FieldRow>
                <FieldRow label="Last Name">
                    <Input
                        placeholder="Last Name"
                        allowClear
                        required
                        value={networkLastName}
                        onChange={e => setNetworkLastName(e.target.value)}
                    />
                </FieldRow>
                <FieldRow label="Email">
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <Input
                            placeholder="Email"
                            allowClear
                            disabled={networkSkipEmail}
                            value={networkEmail}
                            onChange={e => setNetworkEmail(e.target.value)}
                        />
                        <Checkbox
                            checked={networkSkipEmail}
                            onChange={e => setNetworkSkipEmail(e.target.checked)}
                        >
                            <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>Skip email</span>
                        </Checkbox>
                    </div>
                </FieldRow>
                <FieldRow label="Items">
                    <Input
                        placeholder="Items (comma separated)"
                        allowClear
                        required
                        value={networkItems}
                        onChange={e => setNetworkItems(e.target.value)}
                    />
                </FieldRow>
                <FieldRow label="Amount">
                    <Input
                        placeholder="Amount"
                        allowClear
                        required
                        value={networkAmount}
                        onChange={e => {
                            if (/^\d*\.?\d*$/.test(e.target.value)) setNetworkAmount(e.target.value);
                        }}
                    />
                </FieldRow>
                <FieldRow label="VAT %">
                    <Input
                        placeholder="VAT %"
                        allowClear
                        required
                        value={networkVATPercent}
                        onChange={e => {
                            if (/^\d*\.?\d*$/.test(e.target.value)) setNetworkVATPercent(e.target.value);
                        }}
                        addonAfter="%"
                    />
                </FieldRow>
                <FieldRow label="Processing Fee %">
                    <Input
                        placeholder="Processing Fee %"
                        allowClear
                        required
                        value={networkFeePercent}
                        onChange={e => {
                            if (/^\d*\.?\d*$/.test(e.target.value)) setNetworkFeePercent(e.target.value);
                        }}
                        addonAfter="%"
                    />
                </FieldRow>
                <FieldRow label="Total">
                    <Input value={networkTotal} disabled addonBefore="AED" />
                </FieldRow>
            </div>
            {networkHistoryLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "16px" }}>
                    <Spin tip="Loading payment history…" />
                </div>
            ) : networkPaymentHistory.length > 0 ? (
                <Collapse
                    accordion
                    size="small"
                    defaultActiveKey={[String(networkPaymentHistory[0]?.id)]}
                    items={networkPaymentHistory.map((record, idx) => ({
                        key: String(record.id),
                        label: (
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontWeight: 600 }}>#{networkPaymentHistory.length - idx} — {record.current_status}</span>
                                <span style={{ fontSize: 11, color: "gray" }}>
                                    {record.timeline?.[0]?.timestamp
                                        ? new Date(record.timeline[0].timestamp).toLocaleString()
                                        : ""}
                                </span>
                            </div>
                        ),
                        children: (
                            <div style={{ display: "grid", gap: 12 }}>
                                <Timeline
                                    items={record.timeline.map(t => ({
                                        children: (
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: 13 }}>{t.status}</div>
                                                <div style={{ fontSize: 11, color: "gray" }}>
                                                    {new Date(t.timestamp).toLocaleString()}
                                                </div>
                                            </div>
                                        ),
                                    }))}
                                />
                                <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 8 }}>
                                    <div style={{ fontSize: 11, color: "gray", marginBottom: 4 }}>Payment Link</div>
                                    <Tooltip title="Click to copy">
                                        <div
                                            className="cursor-pointer"
                                            style={{ fontSize: 11, wordBreak: "break-all", color: "#1677ff" }}
                                            onClick={() => copyToClipboardModern(record.received_data.paymentLink, "Payment Link")}
                                        >
                                            {record.received_data.paymentLink}
                                        </div>
                                    </Tooltip>
                                </div>
                                <ConfigProvider theme={requestButtonTheme}>
                                    <Button
                                        endIcon={<SendOutlinedIcon />}
                                        variant="contained"
                                        loading={sendingNetworkIds?.has(record.id)}
                                        onClick={() => handleNetworkSendWatiLinkForRecord(record)}
                                    >
                                        SEND TO CUSTOMER ON WATI
                                    </Button>
                                </ConfigProvider>
                            </div>
                        ),
                    }))}
                />
            ) : (
                <div className="border rounded p-2 gap-2 grid" style={{ backgroundColor: "#fbfbfb" }}>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <Typography>Generated Link</Typography>
                    </div>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <Typography>
                            {networkLink ? (
                                <Tooltip title="Click on the link to copy it to the clipboard.">
                                    <div className="cursor-pointer" onClick={() => copyToClipboardModern(networkLink, "Payment Link")}>{networkLink}</div>
                                </Tooltip>
                            ) : (
                                <i style={{ color: "gray" }}>Generate Link First</i>
                            )}
                        </Typography>
                    </div>
                    {networkLink && (
                        <>
                            <div style={{ height: "7px" }}></div>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                <ConfigProvider theme={requestButtonTheme}>
                                    <Button
                                        endIcon={<SendOutlinedIcon />}
                                        variant="contained"
                                        loading={load_send_network_link_to_wati}
                                        onClick={handleNetworkSendWatiLink}
                                    >
                                        SEND TO CUSTOMER ON WATI
                                    </Button>
                                </ConfigProvider>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );

    const mamoTabContent = (
        <div className="gap-2 grid border rounded drop-shadow p-2" style={{ backgroundColor: "#f7f7f7" }}>
            <div className="border rounded p-2 gap-2 grid" style={{ backgroundColor: "#fbfbfb" }}>
                <div className="flex gap-1">
                    <div className="flex justify-between" style={{ width: "120px" }}><span>Title</span><span>:</span></div>
                    <div className="flex gap-1" style={{ width: "75%" }}>
                        <Input
                            maxLength={50}
                            placeholder="Title"
                            allowClear
                            required
                            value={linkTitle}
                            onChange={(e) => setLinkTitleHandler(e.target.value)}
                        />
                        <Tooltip title="Title must be less than 50 characters">
                            <InfoCircleOutlined />
                        </Tooltip>
                    </div>
                </div>
                <div className="flex gap-1 justify-between">
                    <div className="flex justify-between" style={{ width: "120px" }}><span>Description</span><span>:</span></div>
                    <div className="flex gap-1" style={{ width: "75%" }}>
                        <Input
                            placeholder="Description"
                            allowClear
                            required
                            value={linkDescription}
                            onChange={(e) => setLinkDescriptionHandler(e.target.value)}
                        />
                        <Tooltip title="Description must be less than 70 characters">
                            <InfoCircleOutlined />
                        </Tooltip>
                    </div>
                </div>
                <div className="flex gap-1 justify-between">
                    <div className="flex justify-between" style={{ width: "120px" }}><span>Amount</span><span>:</span></div>
                    <div className="flex gap-1" style={{ width: "75%" }}>
                        <Input
                            placeholder="Amount"
                            allowClear
                            required
                            value={linkAmount}
                            onChange={(e) => {
                                if (/^\d*\.?\d*$/.test(e.target.value)) setLinkAmount(e.target.value);
                            }}
                        />
                        <span className="flex">
                            <span className="flex items-center">
                                <Divider type="vertical" />
                            </span>
                            <span className="flex items-center">
                                <Tooltip title="Enable tabby while checkout">
                                    <Checkbox
                                        className="flex items-center"
                                        onChange={(e) => handle_tabby_enable_change(e)}
                                        value={enable_tabby}>
                                        <img src={tabby_logo} className="flex items-center" style={{ width: "70px" }} alt="Tabby" />
                                    </Checkbox>
                                </Tooltip>
                            </span>
                        </span>
                    </div>
                </div>
                <div className="flex gap-1 justify-between">
                    <div className="flex justify-between" style={{ width: "120px" }}><span>VAT %</span><span>:</span></div>
                    <div className="flex gap-1" style={{ width: "75%" }}>
                        <Input
                            placeholder="VAT %"
                            allowClear
                            required
                            value={linkVATPercent}
                            onChange={(e) => {
                                if (/^\d*\.?\d*$/.test(e.target.value)) setLinkVATPercent(e.target.value);
                            }}
                            addonAfter="%"
                        />
                        <Tooltip title="Usually it is 5%">
                            <InfoCircleOutlined />
                        </Tooltip>
                    </div>
                </div>
                <div className="flex gap-1 justify-between">
                    <div className="flex justify-between" style={{ width: "120px" }}><span>Processing Fee %</span><span>:</span></div>
                    <div className="flex gap-1" style={{ width: "75%" }}>
                        <Input
                            placeholder="Processing Fee %"
                            allowClear
                            max={5}
                            disabled={disablePercentEdit}
                            required
                            value={linkFeePercent}
                            onChange={(e) => {
                                if (/^\d*\.?\d*$/.test(e.target.value)) setLinkFeePercent(e.target.value);
                            }}
                            addonAfter="%"
                        />
                        <Tooltip title="Processing fee must be less than 5%">
                            <InfoCircleOutlined />
                        </Tooltip>
                    </div>
                </div>
            </div>
            <div className="flex gap-1 justify-center border rounded" style={{ backgroundColor: "#fbfbfb", width: "100%" }}>
                <div className="flex justify-between p-2 pt-3">
                    <div className="border rounded pl-0.5 pr-0.5 drop-shadow ml-1" style={{ marginBottom: "8px", marginTop: "8px" }}>
                        <div style={{ fontSize: "8px", transform: "translateY(-10px)", marginBottom: "-11px", background: "white", display: "table", borderRadius: "10px", fontWeight: "600" }}>&nbsp;Price&nbsp;</div>
                        <div style={{ fontWeight: "600", fontSize: "16px" }}>{linkAmount}</div>
                    </div>
                    <div className="flex items-center pl-2 pr-2"><PlusOutlined /></div>
                    <div className="border rounded pl-0.5 pr-0.5 drop-shadow ml-1" style={{ marginBottom: "8px", marginTop: "8px" }}>
                        <div style={{ fontSize: "8px", transform: "translateY(-10px)", marginBottom: "-11px", background: "white", display: "table", borderRadius: "10px", fontWeight: "600" }}>&nbsp;VAT&nbsp;</div>
                        <div style={{ fontWeight: "600", fontSize: "16px" }}>
                            {(linkAmount * (linkVATPercent / 100)).toFixed(2) || 0}
                        </div>
                    </div>
                    <div className="flex items-center pl-2 pr-2"><PlusOutlined /></div>
                    <div className="border rounded pl-0.5 pr-0.5 drop-shadow ml-1" style={{ marginBottom: "8px", marginTop: "8px" }}>
                        <div style={{ fontSize: "8px", transform: "translateY(-10px)", marginBottom: "-11px", background: "white", display: "table", borderRadius: "10px", fontWeight: "600" }}>&nbsp;Processing Fee&nbsp;</div>
                        <div style={{ fontWeight: "600", fontSize: "16px" }}>
                            {((parseFloat((linkAmount * (1 + linkVATPercent / 100)).toFixed(2) || 0)) * (linkFeePercent / 100)).toFixed(2) || 0}
                        </div>
                    </div>
                    <div className="flex items-center pl-2 pr-2 rotate-90"><PauseOutlined /></div>
                    <div className="border rounded pl-0.5 pr-0.5 drop-shadow ml-1 pt-1" style={{ fontSize: "22px", fontWeight: "700" }}>
                        <span style={{ fontSize: "18px", color: "gray" }}>AED</span>
                        {(parseFloat(linkAmount) + parseFloat(((linkAmount * (linkVATPercent / 100)) || 0)) + ((parseFloat((linkAmount * (1 + linkVATPercent / 100)) || 0)) * (linkFeePercent / 100)) || 0).toFixed(2)}
                    </div>
                </div>
            </div>
            <div className="border rounded p-2 gap-2 grid" style={{ backgroundColor: "#fbfbfb" }}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <Typography>Generated Link</Typography>
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <Typography>
                        {theLink ? (
                            <Tooltip title="Click on the link to copy it to the clipboard.">
                                <div className="cursor-pointer" onClick={() => copyToClipboardModern(theLink, "Payment Link")}>{theLink}</div>
                            </Tooltip>
                        ) : (
                            <i style={{ color: "gray" }}>Generate Link First</i>
                        )}
                    </Typography>
                </div>
                <div style={{ height: "7px" }}></div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <ConfigProvider theme={requestButtonTheme}>
                        <Button
                            id="link_gen_send_wati"
                            endIcon={<SendOutlinedIcon />}
                            variant="contained"
                            loading={load_send_link_to_wati}
                            onClick={handleSendWatiLink}
                            style={{ display: "none" }}
                        >
                            SEND TO CUSTOMER ON WATI
                        </Button>
                    </ConfigProvider>
                </div>
            </div>
        </div>
    );

    const tabItems = [
        {
            key: "network",
            label: (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <img src={network_logo} alt="Network" style={{ height: 18, borderRadius: 3 }} />
                    Network
                </span>
            ),
            children: networkTabContent,
        },
        {
            key: "mamo",
            label: (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <img src={mamo_logo} alt="Mamo Pay" style={{ height: 18, borderRadius: 3 }} />
                    Mamo Pay
                </span>
            ),
            children: mamoTabContent,
        },
    ];

    return (
        <>
            <Modal
                title={`Generate Pay by link for ${openLinkGen?.data?.query_id || "Unknown Query ID"}`}
                open={openLinkGen?.open}
                onCancel={handleCloseLinkGen}
                centered
                width={580}
                maskClosable={false}
                footer={
                    <div className="flex justify-end">
                        <ConfigProvider theme={requestButtonTheme}>
                            <Button
                                loading={activePayTab === "network" ? network_pay_loading : pay_by_link_loading}
                                endIcon={<AddLinkIcon />}
                                variant="contained"
                                onClick={activePayTab === "network" ? handleNetworkLinkGen : handleLinkGen}
                            >
                                Request Link
                            </Button>
                        </ConfigProvider>
                    </div>
                }
            >
                <Tabs
                    activeKey={activePayTab}
                    onChange={setActivePayTab}
                    items={tabItems}
                />
            </Modal>
        </>
    );
};

export default PayByLinkModal;
